import { ReactNode, useRef, useState } from 'react';
import clsx from 'clsx';

const MIN_COLUMN_WIDTH_PERCENT = 20;
const MAX_COLUMN_WIDTH_PERCENT = 80;
const RESIZE_KEYBOARD_STEP_PERCENT = 5;

/** Two columns split by a draggable divider, so either can be widened at the other's expense. */
export function ResizableColumns({
  left,
  right,
}: {
  left: ReactNode;
  right: ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [leftWidthPercent, setLeftWidthPercent] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  return (
    <div ref={containerRef} className="flex items-start">
      <div
        className="flex min-w-0 flex-col gap-4"
        style={{ width: `calc(${leftWidthPercent}% - 8px)` }}
      >
        {left}
      </div>

      <button
        type="button"
        aria-label="Resize columns"
        onMouseDown={startDrag}
        onKeyDown={handleKeyDown}
        className={clsx(
          'mx-2 w-1 shrink-0 cursor-col-resize self-stretch rounded-full border-0 bg-gray-4 p-0 hover:bg-blue-8 th-highcontrast:bg-gray-6 th-highcontrast:hover:bg-blue-8 th-dark:bg-gray-7 th-dark:hover:bg-blue-8',
          isDragging && '!bg-blue-8'
        )}
      />

      <div className="flex min-w-0 flex-1 flex-col gap-4">{right}</div>
    </div>
  );

  function startDrag(event: React.MouseEvent) {
    event.preventDefault();
    const container = containerRef.current;
    if (!container) {
      return;
    }

    setIsDragging(true);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    function handleMouseMove(moveEvent: MouseEvent) {
      const rect = container!.getBoundingClientRect();
      const percent = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      setLeftWidthPercent(clampColumnWidth(percent));
    }

    function handleMouseUp() {
      setIsDragging(false);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowLeft') {
      setLeftWidthPercent((prev) =>
        clampColumnWidth(prev - RESIZE_KEYBOARD_STEP_PERCENT)
      );
    } else if (event.key === 'ArrowRight') {
      setLeftWidthPercent((prev) =>
        clampColumnWidth(prev + RESIZE_KEYBOARD_STEP_PERCENT)
      );
    }
  }
}

function clampColumnWidth(percent: number): number {
  return Math.min(
    MAX_COLUMN_WIDTH_PERCENT,
    Math.max(MIN_COLUMN_WIDTH_PERCENT, percent)
  );
}
